import { useGetLocale } from '@legacy/components/global/LocaleProvider';
import { HTMLAttributes, ReactNode } from 'react';
import { convertDateToLocaleString } from '@legacy/utils/date';
import { formatUrl } from '@legacy/utils/url';
import { Link } from '@legacy/components/ui-components';
import {
  AvatarGroup,
  Box,
  Card,
  CardHeader,
  Stack,
  Tooltip,
  Typography,
} from '@legacy/components/mui-components';
import { Avatar } from '@mui/material';
import { getUserAvatar } from '@legacy/utils/getUserAvatar';

interface ListItemProps extends HTMLAttributes<HTMLElement> {
  name: string;
  employeeId: number;
  navigationItemType?: number;
  normalWorkingTimeHoursInPeriod?: number;
  hours?: number;
  startDate?: string;
  endDate?: string;
  url?: string;
  linkText?: string;
  allocatedEmployees?: Employee[];
  projectId?: number;
}
type Employee = {
  employeeUserId: number;
  employeeInitials: string;
};
export const ListItem = ({
  name,
  employeeId,
  navigationItemType,
  normalWorkingTimeHoursInPeriod,
  hours,
  startDate,
  endDate,
  url,
  linkText,
  allocatedEmployees,
  projectId,
  ...props
}: ListItemProps) => {
  const siteLocale = useGetLocale();
  const isAbsence = navigationItemType === 1 && startDate && endDate;
  const isProjectTime = navigationItemType === 2;
  const isOneDay = startDate === endDate;
  const isTimeSheet =
    hours !== undefined && normalWorkingTimeHoursInPeriod !== undefined;
  const MAX_VISIBLE = 3;
  const visible = allocatedEmployees?.slice(0, MAX_VISIBLE) ?? [];
  const hidden = allocatedEmployees?.slice(MAX_VISIBLE) ?? [];
  let dateNode: ReactNode = null;
  if (startDate && endDate) {
    if (isOneDay) {
      dateNode = (
        <Typography variant="label" data-automation-id="ListItemAbsenceOneDay">
          {convertDateToLocaleString(new Date(startDate), siteLocale)}
        </Typography>
      );
    } else if (!allocatedEmployees) {
      dateNode = (
        <Typography variant="label" data-automation-id="ListItemAbsenceRange">
          {convertDateToLocaleString(new Date(startDate), siteLocale)} –{' '}
          {convertDateToLocaleString(new Date(endDate), siteLocale)}
        </Typography>
      );
    } else {
      dateNode = (
        <Typography variant="label" data-automation-id="ListItemAbsenceRange">
          {convertDateToLocaleString(new Date(startDate), siteLocale)} –{' '}
          {convertDateToLocaleString(new Date(endDate), siteLocale)}
        </Typography>
      );
    }
  }
  return (
    <Card sx={{ maxWidth: 345 }} elevation={1} {...props}>
      <CardHeader
        avatar={
          !allocatedEmployees && (
            <Avatar src={getUserAvatar(employeeId)} aria-label={name[0]}>
              {name[0]}
            </Avatar>
          )
        }
        title={
          url && linkText ? (
            <Link
              title={linkText}
              href={formatUrl(
                `${url}${
                  // startAbsenceDate is being used in TLP
                  isAbsence
                    ? `&startAbsenceDate=${startDate.split('T')[0]}`
                    : ''
                }${
                  // Add projectId when navigationItemType is 2
                  isProjectTime && projectId ? `&projectId=${projectId}` : ''
                }`,
              )}
              data-automation-id={
                isAbsence
                  ? 'ApproveSingleAbsenceSheetLink'
                  : 'ApproveSingleProjectLink'
              }
            >
              <Typography>{name}</Typography>
            </Link>
          ) : (
            <Typography>{name}</Typography>
          )
        }
        subheader={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            {/* left side: hours + avatar group */}
            {allocatedEmployees && allocatedEmployees.length > 0 && (
              <AvatarGroup>
                {visible.map((emp) => (
                  <Tooltip
                    key={emp.employeeUserId}
                    arrow
                    title={emp.employeeInitials}
                  >
                    <Avatar
                      src={getUserAvatar(emp.employeeUserId)}
                      alt={emp.employeeInitials}
                    >
                      {emp.employeeInitials.charAt(0)}
                    </Avatar>
                  </Tooltip>
                ))}
                {/* custom +N wrapped in a Tooltip */}
                {hidden.length > 0 && (
                  <Tooltip
                    arrow
                    title={
                      <Box display="flex" flexDirection="column" p={0.5}>
                        {hidden.map((employee) => (
                          <Typography
                            key={employee.employeeUserId}
                            variant="small"
                            component="div"
                          >
                            {employee.employeeInitials}
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Avatar>+{hidden.length}</Avatar>
                  </Tooltip>
                )}
              </AvatarGroup>
            )}

            {isTimeSheet && !allocatedEmployees && (
              <Typography
                variant="label"
                fontWeight={600}
                data-automation-id="ListItemHours"
              >
                {`${hours!.toFixed(2)}/${normalWorkingTimeHoursInPeriod!.toFixed(2)}`}
              </Typography>
            )}
            {dateNode}
          </Stack>
        }
      />
    </Card>
  );
};
